import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Mail, Send, Loader2, Save, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = [
  { value: '0', label: 'Montag' },
  { value: '1', label: 'Dienstag' },
  { value: '2', label: 'Mittwoch' },
  { value: '3', label: 'Donnerstag' },
  { value: '4', label: 'Freitag' },
  { value: '5', label: 'Samstag' },
  { value: '6', label: 'Sonntag' },
];

const ReportEmailSettings = () => {
  const [settings, setSettings] = useState({
    enabled: false, frequency: 'weekly', weekday: 0, month_day: 1, admin_email: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/settings/report-email`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettings(res.data);
      } catch (err) {
        console.error(err);
      } finally { setLoading(false); }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/settings/report-email`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data.settings);
      toast.success('Report-Einstellungen gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const handleSendNow = async () => {
    if (!settings.admin_email) { toast.error('Bitte zuerst eine E-Mail-Adresse eingeben'); return; }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/report-email/send-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test-Report gesendet!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Senden');
    } finally { setSending(false); }
  };

  if (loading) {
    return (
      <Card><CardContent className="pt-6 flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </CardContent></Card>
    );
  }

  return (
    <Card data-testid="report-email-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base" data-testid="report-email-settings-title">
          <Mail className="w-5 h-5 text-amber-500" />
          Report-E-Mail
        </CardTitle>
        <CardDescription>
          Automatischer KI-Performance + Website-Analytics Report per E-Mail
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Enable Toggle */}
        <div className="flex items-center justify-between" data-testid="report-email-enabled-toggle">
          <div>
            <Label className="font-medium">Report aktivieren</Label>
            <p className="text-xs text-muted-foreground">Automatischen E-Mail-Versand einschalten</p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(v) => setSettings(prev => ({ ...prev, enabled: v }))}
            data-testid="report-email-enabled-switch"
          />
        </div>

        {/* Admin Email */}
        <div>
          <Label htmlFor="report-admin-email" className="text-sm">Empfänger (Admin-E-Mail)</Label>
          <Input
            id="report-admin-email"
            type="email"
            value={settings.admin_email || ''}
            onChange={(e) => setSettings(prev => ({ ...prev, admin_email: e.target.value }))}
            placeholder="admin@strongfit.ch"
            className="mt-1"
            data-testid="report-email-address-input"
          />
        </div>

        {/* Frequency */}
        <div>
          <Label className="text-sm">Frequenz</Label>
          <Select
            value={settings.frequency}
            onValueChange={(v) => setSettings(prev => ({ ...prev, frequency: v }))}
          >
            <SelectTrigger className="mt-1" data-testid="report-email-frequency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Täglich</SelectItem>
              <SelectItem value="weekly">Wöchentlich</SelectItem>
              <SelectItem value="monthly">Monatlich</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Weekday (only for weekly) */}
        {settings.frequency === 'weekly' && (
          <div>
            <Label className="text-sm">Wochentag</Label>
            <Select
              value={String(settings.weekday ?? 0)}
              onValueChange={(v) => setSettings(prev => ({ ...prev, weekday: parseInt(v) }))}
            >
              <SelectTrigger className="mt-1" data-testid="report-email-weekday-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEEKDAYS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Month Day (only for monthly) */}
        {settings.frequency === 'monthly' && (
          <div>
            <Label htmlFor="report-month-day" className="text-sm">Tag im Monat</Label>
            <Input
              id="report-month-day"
              type="number"
              min={1}
              max={28}
              value={settings.month_day ?? 1}
              onChange={(e) => setSettings(prev => ({ ...prev, month_day: parseInt(e.target.value) || 1 }))}
              className="mt-1"
              data-testid="report-email-monthday-input"
            />
          </div>
        )}

        {/* Last Sent */}
        {settings.last_sent && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="report-email-last-sent">
            <Clock className="w-3.5 h-3.5" />
            Letzter Versand: {new Date(settings.last_sent).toLocaleString('de-CH')}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="report-email-save-button">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Speichern
          </Button>
          <Button
            variant="outline"
            onClick={handleSendNow}
            disabled={sending || !settings.admin_email}
            data-testid="report-email-send-now-button"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Jetzt senden
          </Button>
        </div>

        {settings.enabled && (
          <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300" data-testid="report-email-active-badge">
            Aktiv – {settings.frequency === 'daily' ? 'Täglich' : settings.frequency === 'weekly' ? `Jeden ${WEEKDAYS[settings.weekday]?.label || 'Montag'}` : `Am ${settings.month_day}. des Monats`} um 07:30
          </Badge>
        )}
      </CardContent>
    </Card>
  );
};

export default ReportEmailSettings;
